/**
 * 교시별 출결 현황 페이지
 *
 * - 룸 선택 + 날짜 선택 → 학생 × 활성 교시 매트릭스 표시
 * - 좌석번호순 정렬, KST 표시
 * - 엑셀(.xlsx) 다운로드
 *
 * 출결 표기:
 *   ○ 출석  △ 지각  × 결석  ◇ 외출  □ 사전결석  - 해당없음
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';
import './PeriodAttendanceGridPage.css';

interface RoomItem {
  id: string;
  name: string;
  display_order?: number;
}

interface PeriodInfo {
  period_number: number;
  period_name: string;
  period_type: string;
  start_time: string;
  end_time: string;
}

interface StudentRow {
  student_id: string;
  name: string;
  seat_number: number;
  grade?: string | null;
  class_name?: string | null;
  statuses: Record<string, string>;
  phone?: string | null;
  parent_phone?: string | null;
  parent_phone_2?: string | null;
}

interface FilterOptions {
  grades: string[];
  classes: string[];
}

interface GridResponse {
  room_id: string;
  room_name: string;
  date: string;
  periods: PeriodInfo[];
  students: StudentRow[];
  filter_options?: FilterOptions;
  summary: { total_students: number; total_periods: number };
}

type SortBy = 'seat' | 'grade_class' | 'name';

// 교시 상세 (교시 헤더 클릭 시)
interface PatrolNote {
  note_type: string;
  note_type_label: string;
  note_text: string;
  time: string;
}
interface PeriodDetailStudent {
  student_id: string;
  name: string;
  seat_number: number;
  grade?: string | null;
  class_name?: string | null;
  entry_time?: string | null;
  exit_time?: string | null;
  exception?: { type: string; reason: string } | null;
  patrol_notes: PatrolNote[];
}
interface PeriodDetail {
  period_number: number;
  period_name: string;
  start_time: string;
  end_time: string;
  students: PeriodDetailStudent[];
}

const STATUS_TEXT: Record<string, string> = {
  present: '출석',
  late: '지각',
  absent: '결석',
  outing: '외출',
  absent_excused: '사유결석',
  no_schedule: '해당없음',
};

const STATUS_LABEL: Record<string, string> = {
  present: '○',
  late: '△',
  absent: '×',
  outing: '◇',
  absent_excused: '□',
  no_schedule: '-',
};

const STATUS_CLASS: Record<string, string> = {
  present: 'cell-present',
  late: 'cell-late',
  absent: 'cell-absent',
  outing: 'cell-outing',
  absent_excused: 'cell-excused',
  no_schedule: 'cell-none',
};

// 전화번호 → tel: 링크용 (숫자/＋ 만 남김). 폰에서 누르면 통화앱이 번호 채운 채 뜸.
const telHref = (p?: string | null) => (p ? p.replace(/[^0-9+]/g, '') : '');

const todayKstString = (): string => {
  // KST(Asia/Seoul) 기준 YYYY-MM-DD
  const now = new Date();
  const kstOffsetMs = 9 * 60 * 60 * 1000;
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60 * 1000;
  const kst = new Date(utcMs + kstOffsetMs);
  const y = kst.getFullYear();
  const m = String(kst.getMonth() + 1).padStart(2, '0');
  const d = String(kst.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getAuthToken = (): string | null =>
  localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');

const PeriodAttendanceGridPage: React.FC = () => {
  const [rooms, setRooms] = useState<RoomItem[]>([]);
  const [roomId, setRoomId] = useState<string>('');
  const [targetDate, setTargetDate] = useState<string>(todayKstString());
  const [grid, setGrid] = useState<GridResponse | null>(null);
  const [loadingRooms, setLoadingRooms] = useState<boolean>(false);
  const [loadingGrid, setLoadingGrid] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 필터 + 정렬
  const [gradeFilter, setGradeFilter] = useState<string>('');
  const [classFilter, setClassFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortBy>('seat');
  // 룸 첫 진입 시 필터 옵션을 알기 위해 별도로 옵션 캐시 보유
  const [optionsCache, setOptionsCache] = useState<FilterOptions>({ grades: [], classes: [] });

  // 교시 상세 (헤더 클릭 → 표 아래 펼쳐짐)
  const [expandedPeriod, setExpandedPeriod] = useState<number | null>(null);
  const [periodDetail, setPeriodDetail] = useState<PeriodDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);

  const loadRooms = useCallback(async () => {
    try {
      setLoadingRooms(true);
      setError(null);
      const token = getAuthToken();
      const res = await fetch(`${API_BASE_URL}/v1/owner/rooms`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        throw new Error(`룸 목록 조회 실패 (${res.status})`);
      }
      const data: RoomItem[] = await res.json();
      const sorted = [...data].sort((a, b) => {
        const oa = a.display_order ?? 0;
        const ob = b.display_order ?? 0;
        if (oa !== ob) return oa - ob;
        return (a.name || '').localeCompare(b.name || '', 'ko');
      });
      setRooms(sorted);
      if (sorted.length > 0 && !roomId) {
        setRoomId(sorted[0].id);
      }
    } catch (e: any) {
      setError(e?.message || '룸 목록을 불러오지 못했습니다.');
    } finally {
      setLoadingRooms(false);
    }
  }, [roomId]);

  const buildQuery = useCallback((): string => {
    const params = new URLSearchParams();
    params.set('room_id', roomId);
    params.set('target_date', targetDate);
    if (gradeFilter) params.set('grade', gradeFilter);
    if (classFilter) params.set('class_name', classFilter);
    if (sortBy) params.set('sort_by', sortBy);
    return params.toString();
  }, [roomId, targetDate, gradeFilter, classFilter, sortBy]);

  // 교시 헤더 클릭 → 그 교시 상세 (입실·사유서·순찰). 같은 교시 다시 누르면 접힘.
  const togglePeriodDetail = useCallback(
    async (periodNumber: number) => {
      if (expandedPeriod === periodNumber) {
        setExpandedPeriod(null);
        setPeriodDetail(null);
        return;
      }
      setExpandedPeriod(periodNumber);
      setPeriodDetail(null);
      setLoadingDetail(true);
      try {
        const token = getAuthToken();
        const params = new URLSearchParams();
        params.set('room_id', roomId);
        params.set('target_date', targetDate);
        params.set('period_number', String(periodNumber));
        const res = await fetch(
          `${API_BASE_URL}/v1/owner/attendance-period-grid/period-detail?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
        );
        if (!res.ok) throw new Error(`교시 상세 조회 실패 (${res.status})`);
        setPeriodDetail(await res.json());
      } catch (e: any) {
        setError(e?.message || '교시 상세를 불러오지 못했습니다.');
        setExpandedPeriod(null);
      } finally {
        setLoadingDetail(false);
      }
    },
    [expandedPeriod, roomId, targetDate],
  );

  const loadGrid = useCallback(async () => {
    if (!roomId || !targetDate) return;
    try {
      setLoadingGrid(true);
      setError(null);
      // 룸/날짜 바뀌면 열려있던 교시 상세는 닫음(엉뚱한 교시 상세 방지)
      setExpandedPeriod(null);
      setPeriodDetail(null);
      const token = getAuthToken();
      const url = `${API_BASE_URL}/v1/owner/attendance-period-grid?${buildQuery()}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(`그리드 조회 실패 (${res.status}): ${detail}`);
      }
      const data: GridResponse = await res.json();
      setGrid(data);
      if (data.filter_options) {
        setOptionsCache(data.filter_options);
      }
    } catch (e: any) {
      setError(e?.message || '교시별 출결 데이터를 불러오지 못했습니다.');
      setGrid(null);
    } finally {
      setLoadingGrid(false);
    }
  }, [roomId, targetDate, buildQuery]);

  const handleExcelDownload = useCallback(async () => {
    if (!roomId || !targetDate) return;
    try {
      setDownloading(true);
      setError(null);
      const token = getAuthToken();
      const url = `${API_BASE_URL}/v1/owner/attendance-period-grid/export?${buildQuery()}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        throw new Error(`엑셀 다운로드 실패 (${res.status})`);
      }
      const blob = await res.blob();

      // Content-Disposition에서 파일명 추출 시도
      let filename = `교시별출결_${targetDate}.xlsx`;
      const cd = res.headers.get('Content-Disposition');
      if (cd) {
        const m = cd.match(/filename\*=UTF-8''([^;]+)/i);
        if (m && m[1]) {
          try {
            filename = decodeURIComponent(m[1]);
          } catch {
            // 무시
          }
        } else {
          const m2 = cd.match(/filename="([^"]+)"/i);
          if (m2 && m2[1]) {
            filename = m2[1];
          }
        }
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (e: any) {
      setError(e?.message || '엑셀 다운로드에 실패했습니다.');
    } finally {
      setDownloading(false);
    }
  }, [roomId, targetDate, buildQuery]);

  useEffect(() => {
    loadRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (roomId && targetDate) {
      loadGrid();
    }
  }, [roomId, targetDate, gradeFilter, classFilter, sortBy, loadGrid]);

  // 룸이 바뀌면 학년/반 필터 초기화 (새 룸의 옵션이 다를 수 있음)
  useEffect(() => {
    setGradeFilter('');
    setClassFilter('');
  }, [roomId]);

  const dateDisplayKst = useMemo(() => {
    if (!targetDate) return '';
    return `${targetDate} (KST)`;
  }, [targetDate]);

  return (
    <div className="period-grid-page">
      <header className="period-grid-header">
        <h1>교시별 출결 현황</h1>
        <p className="period-grid-sub">
          좌석 배정된 학생의 활성 교시별 출석/지각/결석 상태를 확인하고 엑셀로 추출합니다.
        </p>
      </header>

      <section className="period-grid-controls">
        <div className="control-item">
          <label htmlFor="room-select">룸</label>
          <select
            id="room-select"
            value={roomId}
            onChange={(e) => setRoomId(e.target.value)}
            disabled={loadingRooms}
          >
            {rooms.length === 0 && <option value="">(룸 없음)</option>}
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>

        <div className="control-item">
          <label htmlFor="date-input">날짜 (KST)</label>
          <input
            id="date-input"
            type="date"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
          />
        </div>

        <div className="control-item">
          <label htmlFor="grade-select">학년</label>
          <select
            id="grade-select"
            value={gradeFilter}
            onChange={(e) => setGradeFilter(e.target.value)}
            disabled={optionsCache.grades.length === 0}
          >
            <option value="">전체</option>
            {optionsCache.grades.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>

        <div className="control-item">
          <label htmlFor="class-select">반</label>
          <select
            id="class-select"
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            disabled={optionsCache.classes.length === 0}
          >
            <option value="">전체</option>
            {optionsCache.classes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="control-item">
          <label htmlFor="sort-select">정렬</label>
          <select
            id="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
          >
            <option value="seat">좌석번호순</option>
            <option value="grade_class">학년→반→좌석순</option>
            <option value="name">이름순</option>
          </select>
        </div>

        <div className="control-actions">
          <button
            className="btn btn-secondary"
            onClick={loadGrid}
            disabled={loadingGrid || !roomId || !targetDate}
          >
            {loadingGrid ? '불러오는 중…' : '새로고침'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleExcelDownload}
            disabled={downloading || !roomId || !targetDate || !grid}
          >
            {downloading ? '다운로드 중…' : '엑셀 다운로드'}
          </button>
        </div>
      </section>

      {error && <div className="period-grid-error">{error}</div>}

      <section className="period-grid-info">
        {grid && (
          <div className="info-row">
            <span><strong>룸:</strong> {grid.room_name}</span>
            <span><strong>날짜:</strong> {dateDisplayKst}</span>
            <span><strong>학생 수:</strong> {grid.summary.total_students}</span>
            <span><strong>활성 교시:</strong> {grid.summary.total_periods}</span>
          </div>
        )}
        <div className="legend">
          <span className="legend-item"><i className="cell-present">○</i> 출석</span>
          <span className="legend-item"><i className="cell-late">△</i> 지각</span>
          <span className="legend-item"><i className="cell-absent">×</i> 결석</span>
          <span className="legend-item"><i className="cell-outing">◇</i> 외출</span>
          <span className="legend-item"><i className="cell-excused">□</i> 사전결석</span>
          <span className="legend-item"><i className="cell-none">-</i> 해당없음</span>
        </div>
      </section>

      <section className="period-grid-table-wrap">
        {loadingGrid ? (
          <div className="grid-loading">데이터를 불러오는 중…</div>
        ) : grid && grid.periods.length === 0 ? (
          <div className="grid-empty">활성화된 교시가 없습니다. 교시 시간표 설정을 먼저 진행해주세요.</div>
        ) : grid && grid.students.length === 0 ? (
          <div className="grid-empty">선택한 룸에 좌석 배정된 학생이 없습니다.</div>
        ) : grid ? (
          <table className="period-grid-table">
            <thead>
              <tr>
                <th className="col-seat">좌석</th>
                <th className="col-grade">학년</th>
                <th className="col-class">반</th>
                <th className="col-name">학생명</th>
                <th className="col-contact">연락</th>
                {grid.periods.map((p) => {
                  const on = expandedPeriod === p.period_number;
                  return (
                    <th
                      key={p.period_number}
                      className="col-period"
                      onClick={() => togglePeriodDetail(p.period_number)}
                      title="클릭하면 이 교시 상세(입실·사유서·순찰)를 봅니다"
                      style={{
                        cursor: 'pointer',
                        userSelect: 'none',
                        background: on ? '#e0edff' : undefined,
                      }}
                    >
                      <div className="period-name">
                        {p.period_name} <span style={{ fontSize: 10, color: '#3b82f6' }}>{on ? '▾' : '▸'}</span>
                      </div>
                      <div className="period-time">
                        {p.start_time}~{p.end_time}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {grid.students.map((s) => (
                <tr key={s.student_id}>
                  <td className="col-seat">{s.seat_number}</td>
                  <td className="col-grade">{s.grade || '-'}</td>
                  <td className="col-class">{s.class_name || '-'}</td>
                  <td className="col-name">{s.name}</td>
                  <td className="col-contact">
                    <div className="flex gap-1 justify-center">
                      {s.phone ? (
                        <a
                          href={`tel:${telHref(s.phone)}`}
                          title={`학생에게 전화 (${s.phone})`}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 whitespace-nowrap"
                        >
                          📞 학생
                        </a>
                      ) : null}
                      {s.parent_phone ? (
                        <a
                          href={`tel:${telHref(s.parent_phone)}`}
                          title={`학부모에게 전화 (${s.parent_phone})`}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100 whitespace-nowrap"
                        >
                          📞 학부모
                        </a>
                      ) : null}
                      {!s.phone && !s.parent_phone ? (
                        <span className="text-slate-300 text-xs">-</span>
                      ) : null}
                    </div>
                  </td>
                  {grid.periods.map((p) => {
                    const code = s.statuses[String(p.period_number)] ?? 'no_schedule';
                    return (
                      <td
                        key={p.period_number}
                        className={`col-period status-cell ${STATUS_CLASS[code] || ''}`}
                      >
                        {STATUS_LABEL[code] || '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="grid-empty">조회 조건을 선택해주세요.</div>
        )}

        {/* 교시 상세 (헤더 클릭 시 표 아래 펼쳐짐) */}
        {expandedPeriod !== null && (
          <PeriodDetailPanel
            loading={loadingDetail}
            detail={periodDetail}
            statusByStudent={
              grid
                ? Object.fromEntries(
                    grid.students.map((s) => [s.student_id, s.statuses[String(expandedPeriod)] ?? 'no_schedule']),
                  )
                : {}
            }
            onClose={() => {
              setExpandedPeriod(null);
              setPeriodDetail(null);
            }}
          />
        )}
      </section>
    </div>
  );
};

// ── 교시 상세 패널 ──────────────────────────────────────────
const NOTE_EMOJI: Record<string, string> = {
  sleep: '😴',
  off_task: '🌀',
  leave: '🚶',
  bathroom: '🚻',
  phone: '📱',
  return: '↩️',
  focus: '🎯',
  normal: '✅',
  other: '📝',
};

const PeriodDetailPanel: React.FC<{
  loading: boolean;
  detail: PeriodDetail | null;
  statusByStudent: Record<string, string>;
  onClose: () => void;
}> = ({ loading, detail, statusByStudent, onClose }) => {
  const badge = (code: string) => {
    const cls =
      code === 'present'
        ? 'bg-green-100 text-green-700'
        : code === 'late'
          ? 'bg-amber-100 text-amber-700'
          : code === 'absent'
            ? 'bg-red-100 text-red-700'
            : code === 'outing'
              ? 'bg-blue-100 text-blue-800'
            : code === 'absent_excused'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-slate-100 text-slate-400';
    return (
      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${cls}`}>
        {STATUS_TEXT[code] || code}
      </span>
    );
  };

  return (
    <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50/40 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-blue-100/60">
        <div className="font-semibold text-slate-800">
          {detail ? `${detail.period_name} 상세` : '교시 상세'}
          {detail && (
            <span className="text-xs text-slate-500 ml-2 font-normal">
              {detail.start_time}~{detail.end_time} · 입실 시각 · 사유서 · 순찰 기록
            </span>
          )}
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-sm px-2 py-0.5 rounded hover:bg-blue-200/60">
          닫기 ✕
        </button>
      </div>

      {loading ? (
        <div className="px-4 py-8 text-center text-slate-500 text-sm">불러오는 중…</div>
      ) : !detail || detail.students.length === 0 ? (
        <div className="px-4 py-8 text-center text-slate-500 text-sm">표시할 학생이 없습니다.</div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm bg-white">
            <thead className="bg-slate-50 text-slate-500 text-xs sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-medium">좌석</th>
                <th className="text-left px-3 py-2 font-medium">학생</th>
                <th className="text-left px-3 py-2 font-medium">상태</th>
                <th className="text-left px-3 py-2 font-medium">입실</th>
                <th className="text-left px-3 py-2 font-medium">사유서</th>
                <th className="text-left px-3 py-2 font-medium">순찰 기록</th>
              </tr>
            </thead>
            <tbody>
              {detail.students.map((s) => {
                const code = statusByStudent[s.student_id] || 'no_schedule';
                return (
                  <tr key={s.student_id} className="border-t border-slate-100 align-top">
                    <td className="px-3 py-2 text-slate-500">{s.seat_number}</td>
                    <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">
                      {s.name}
                      {(s.grade || s.class_name) && (
                        <span className="text-xs text-slate-400 ml-1">
                          {s.grade || ''}
                          {s.class_name ? ` ${s.class_name}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">{badge(code)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {s.entry_time ? (
                        <span className="text-slate-700">{s.entry_time}</span>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                      {s.exit_time && <span className="text-slate-400 text-xs"> ~{s.exit_time}</span>}
                    </td>
                    <td className="px-3 py-2">
                      {s.exception ? (
                        <span className="inline-block px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">
                          {s.exception.reason || s.exception.type}
                        </span>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {s.patrol_notes.length === 0 ? (
                        <span className="text-slate-300">-</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {s.patrol_notes.map((n, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-xs"
                              title={n.note_text}
                            >
                              {NOTE_EMOJI[n.note_type] || '📝'} {n.note_type_label}
                              <span className="text-slate-400">{n.time}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PeriodAttendanceGridPage;
